import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Link2, ShieldCheck, Archive } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";
import { useAuth } from "@/context/AuthContext";
import { EntityLinkManager } from "@/components/EntityLinkManager";

const NEXT = {
  monitoring: ["investigating", "closed"],
  investigating: ["verified", "disputed", "unresolved", "monitoring", "closed"],
  verified: ["disputed", "unresolved", "closed"],
  disputed: ["investigating", "verified", "unresolved", "closed"],
  unresolved: ["investigating", "verified", "disputed", "closed"],
  closed: ["monitoring", "investigating"],
};
const PRIORITIES = ["low", "medium", "high", "critical"];

function Section({ title, children, action }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-tactical font-bold text-sm">{title}</h3>{action}
      </div>
      {children}
    </Card>
  );
}

export default function GhanaIntelDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canReview = user?.roles?.some((r) => ["super_admin", "editor"].includes(r));
  const [rec, setRec] = useState(null);
  const [notes, setNotes] = useState("");
  const [linkModal, setLinkModal] = useState(null); // {type, options, selected}

  const load = useCallback(() => {
    api.get(`/ghana-intel/records/${id}`).then((r) => { setRec(r.data); setNotes(r.data.analyst_notes || ""); }).catch(() => toast.error("Not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveNotes = async () => {
    try { await api.patch(`/ghana-intel/records/${id}`, { analyst_notes: notes }); toast.success("Notes saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const changePriority = async (priority) => {
    try { await api.patch(`/ghana-intel/records/${id}`, { priority }); toast.success("Priority updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const transition = async (target) => {
    try { await api.post(`/ghana-intel/records/${id}/transition`, { target }); toast.success(`Status → ${target}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const archive = async () => {
    try { await api.delete(`/ghana-intel/records/${id}`); toast.success("Archived"); nav("/command-center/ghana-intel"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openLink = async (type) => {
    const map = { source: "/sources", claim: "/claims", media: "/media/assets", related: "/ghana-intel/records" };
    const key = { source: "linked_sources", claim: "linked_claims", media: "linked_media", related: "related_records" }[type];
    try {
      const { data } = await api.get(map[type]);
      const options = (type === "related" ? data.filter((x) => x.id !== id) : data);
      const selected = (rec[key] || []).map((x) => x.id);
      setLinkModal({ type, options, selected });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLinks = async () => {
    try {
      await api.post(`/ghana-intel/records/${id}/links`, { link_type: linkModal.type, ids: linkModal.selected });
      toast.success("Links updated");
      setLinkModal(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSel = (v) => setLinkModal((m) => ({ ...m, selected: m.selected.includes(v) ? m.selected.filter((x) => x !== v) : [...m.selected, v] }));

  if (!rec) return <div className="p-6 text-zinc-500">Loading…</div>;
  const label = (o, type) => type === "source" ? o.title : type === "claim" ? o.statement : type === "media" ? o.original_filename : o.title;

  return (
    <div className="p-6" data-testid="gi-detail">
      <button onClick={() => nav("/command-center/ghana-intel")} className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"><ArrowLeft className="h-4 w-4" /> Ghana Intelligence</button>
      <PageHeader title={rec.title} subtitle={rec.summary}>
        {canReview && <Btn variant="danger" data-testid="gi-archive-btn" onClick={archive}><Archive className="h-4 w-4" /> Archive</Btn>}
      </PageHeader>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Status & Verification">
            <div className="flex items-center gap-3 mb-3">
              <span className="label-caps text-zinc-500">Current</span>
              <span data-testid="gi-current-status" className="font-mono-data uppercase text-sm text-emerald-400">{rec.status}</span>
              {!rec.has_supporting_evidence && <span className="text-xs text-amber-400">(link a source/claim to enable Verified)</span>}
            </div>
            {canReview ? (
              <div className="flex flex-wrap gap-2" data-testid="gi-transitions">
                {(NEXT[rec.status] || []).map((t) => (
                  <Btn key={t} variant="ghost" data-testid={`gi-transition-${t}`} onClick={() => transition(t)}>
                    {t === "verified" && <ShieldCheck className="h-4 w-4" />} {t}
                  </Btn>
                ))}
              </div>
            ) : <p className="text-xs text-zinc-600">Verification decisions require an editor.</p>}
          </Section>

          <Section title="Analyst Notes">
            <textarea data-testid="gi-notes" className={inputCls} rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} />
            <Btn data-testid="gi-save-notes" onClick={saveNotes} className="mt-2"><Save className="h-4 w-4" /> Save Notes</Btn>
          </Section>

          <Section title="Linked Sources" action={<Btn variant="ghost" data-testid="gi-link-source" onClick={() => openLink("source")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_sources.length === 0 ? <p className="text-xs text-zinc-600">No sources linked.</p> :
              <ul className="space-y-1 text-sm">{rec.linked_sources.map((s) => <li key={s.id} className="text-zinc-300">• {s.title}</li>)}</ul>}
          </Section>

          <Section title="Linked Claims & Evidence" action={<Btn variant="ghost" data-testid="gi-link-claim" onClick={() => openLink("claim")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_claims.length === 0 ? <p className="text-xs text-zinc-600">No claims linked.</p> :
              <ul className="space-y-2 text-sm">{rec.linked_claims.map((c) => (
                <li key={c.id} className="text-zinc-300">• {c.statement}
                  <span className="text-xs text-zinc-500"> — {c.evidence?.length || 0} evidence{c.verdict ? `, verdict: ${c.verdict}` : ""}</span>
                </li>))}</ul>}
          </Section>

          <Section title="Linked Media" action={<Btn variant="ghost" data-testid="gi-link-media" onClick={() => openLink("media")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.linked_media.length === 0 ? <p className="text-xs text-zinc-600">No media linked.</p> :
              <ul className="text-sm text-zinc-300">{rec.linked_media.map((m) => <li key={m.id}>• {m.original_filename} <span className="text-xs text-zinc-500">({m.visibility})</span></li>)}</ul>}
          </Section>

          <Section title="Related Intelligence" action={<Btn variant="ghost" data-testid="gi-link-related" onClick={() => openLink("related")}><Link2 className="h-4 w-4" /> Manage</Btn>}>
            {rec.related_records.length === 0 ? <p className="text-xs text-zinc-600">No related records.</p> :
              <ul className="text-sm">{rec.related_records.map((r) => <li key={r.id} className="text-zinc-300 hover:text-white cursor-pointer" onClick={() => nav(`/command-center/ghana-intel/${r.id}`)}>• {r.title}</li>)}</ul>}
          </Section>

          <EntityLinkManager linkType="intel" recordId={id} note="Explicitly link canonical Entity Profiles (people, parties, ministries, agencies, companies, NGOs, etc.). Records read live from the entity's cross-system view." />
        </div>

        <div className="space-y-4">
          <Section title="Details">
            <dl className="text-sm space-y-2">
              <div><dt className="label-caps text-zinc-500">Topic</dt><dd className="text-zinc-300">{rec.topic || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Regions</dt><dd className="text-zinc-300">{(rec.regions || []).join(", ") || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Institutions</dt><dd className="text-zinc-300">{(rec.institution_details || []).map((i) => i.name).join(", ") || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Entities</dt><dd className="text-zinc-300">{(rec.entities || []).join(", ") || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Event date</dt><dd className="text-zinc-300">{rec.event_date || "—"}</dd></div>
              <div><dt className="label-caps text-zinc-500">Reliability</dt><dd className="text-zinc-300">{rec.reliability || "—"}</dd></div>
              <div>
                <dt className="label-caps text-zinc-500">Priority</dt>
                <dd><select data-testid="gi-priority-select" className={inputCls} value={rec.priority} onChange={(e) => changePriority(e.target.value)}>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</select></dd>
              </div>
            </dl>
          </Section>

          <Section title="Activity / Audit">
            <ul className="space-y-2 text-xs" data-testid="gi-audit">
              {rec.audit_history.length === 0 && <li className="text-zinc-600">No activity.</li>}
              {rec.audit_history.map((a) => (
                <li key={a.id} className="text-zinc-400">
                  <span className="font-mono-data text-emerald-400">{a.action}</span> · {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>

      <Modal open={!!linkModal} onClose={() => setLinkModal(null)} title={`Link ${linkModal?.type}`} wide>
        {linkModal && (
          <div className="space-y-3">
            <div className="max-h-80 overflow-y-auto space-y-1" data-testid="gi-link-options">
              {linkModal.options.length === 0 && <p className="text-xs text-zinc-600">Nothing available. Create one in its own workspace first.</p>}
              {linkModal.options.map((o) => (
                <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
                  <input type="checkbox" checked={linkModal.selected.includes(o.id)} onChange={() => toggleSel(o.id)} />
                  <span className="truncate">{label(o, linkModal.type)}</span>
                </label>
              ))}
            </div>
            <Btn data-testid="gi-save-links" onClick={saveLinks} className="w-full justify-center">Save Links</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
